// === CAPTURE DE LOGS GLOBALE ===
// Ring buffer (50 entrées max) qui collecte console.warn / console.error +
// window.onerror + unhandledrejection. Joint au bug report pour aider le repro.

const MAX_ENTRIES = 50;

export interface LogEntry {
  /** ms epoch */
  ts: number;
  /** error | warn | uncaught | unhandled */
  level: 'error' | 'warn' | 'uncaught' | 'unhandled';
  message: string;
}

export class LogCapture {
  private static buffer: LogEntry[] = [];
  private static initialized = false;

  /** À appeler une seule fois au boot, AVANT que les scènes loggent. */
  static init(): void {
    if (LogCapture.initialized) return;
    LogCapture.initialized = true;

    const origError = console.error.bind(console);
    const origWarn = console.warn.bind(console);

    console.error = (...args: unknown[]) => {
      LogCapture.push('error', LogCapture.format(args));
      origError(...args);
    };
    console.warn = (...args: unknown[]) => {
      LogCapture.push('warn', LogCapture.format(args));
      origWarn(...args);
    };

    // Erreurs JS non capturées (en plus du listener existant dans main.ts)
    window.addEventListener('error', (e) => {
      const where = e.filename ? ` @ ${e.filename.split('/').pop()}:${e.lineno}` : '';
      LogCapture.push('uncaught', `${e.message}${where}`);
    });

    // Promesses rejetées sans .catch
    window.addEventListener('unhandledrejection', (e) => {
      const reason = e.reason instanceof Error ? `${e.reason.message}` : String(e.reason);
      LogCapture.push('unhandled', reason);
    });
  }

  /** Retourne les entrées récentes (du plus ancien au plus récent). */
  static getEntries(): LogEntry[] {
    return [...LogCapture.buffer];
  }

  /** Format texte prêt à attacher à un rapport. */
  static formatText(): string {
    if (LogCapture.buffer.length === 0) return '(aucun log capturé)';
    return LogCapture.buffer.map((e) => {
      const dt = new Date(e.ts).toISOString().slice(11, 23);
      return `[${dt}] ${e.level.toUpperCase()} ${e.message}`;
    }).join('\n');
  }

  private static push(level: LogEntry['level'], message: string): void {
    LogCapture.buffer.push({ ts: Date.now(), level, message: message.slice(0, 500) });
    if (LogCapture.buffer.length > MAX_ENTRIES) LogCapture.buffer.shift();
  }

  private static format(args: unknown[]): string {
    return args.map((a) => {
      if (a instanceof Error) return `${a.name}: ${a.message}`;
      if (typeof a === 'object') {
        try { return JSON.stringify(a); } catch { return String(a); }
      }
      return String(a);
    }).join(' ');
  }
}
